import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados do motor de alçadas genéricas.
 *
 * Mesmo desenho das outras telas de leitura (operacao/comum/dados.ts):
 * nenhum componente chama `chamarApi` direto, e a chave de cache mora aqui,
 * junto de quem a usa.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. `/alcadas/
 * regras` ainda não existe na API — hoje só existem os módulos `eu`, `govbr` e
 * `senha` em `apps/api`. Decisão do dono de 2026-08-26 (CLAUDE.md, "Proibido",
 * exceção estendida) para demonstração ao cliente — mesmas três condições dos
 * cinco módulos de cadastro: só leitura (esta tela já nasceu sem ação de
 * gravação), dado claramente fictício, removido antes de `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`).
 */

export const CHAVES_DE_ALCADAS_GENERICAS = {
  regras: ['alcadas-genericas', 'regras'] as const,
};

/** Uma condição de faixa de valor associada a uma regra de alçada. */
export interface CondicaoDeAlcada {
  valor_minimo?: number;
  valor_maximo?: number;
}

/** Uma regra de aprovação avaliada pelo motor de alçadas. */
export interface RegraDeAlcada {
  id: string;
  codigo: string;
  nome: string;
  contexto: string;
  condicao: CondicaoDeAlcada | null;
  papel_aprovador: string;
  exige_justificativa: boolean;
  prazo_aprovacao_dias_uteis: number | null;
  ordem: number;
  ativo: boolean;
}

/** Rótulo amigável para cada contexto de negócio em que a alçada se aplica. */
export const CONTEXTO_LABEL: Record<string, string> = {
  DEFESA_INTEMPESTIVA: 'Defesa intempestiva (multa)',
  GLOSA_RETROATIVA: 'Glosa retroativa em competência fechada',
  SUBSTITUICAO_LOTE: 'Substituição em lote de veículo',
  ALTERACAO_DEMONSTRATIVO_POS_PAGO: 'Alteração de demonstrativo já pago',
};

/** Descreve a faixa de valor de uma condição, para exibição na tabela. */
export function descreverCondicao(condicao: CondicaoDeAlcada | null): string {
  if (!condicao) {
    return '—';
  }
  const partes: string[] = [];
  if (condicao.valor_minimo != null) {
    partes.push(`≥ ${condicao.valor_minimo}`);
  }
  if (condicao.valor_maximo != null) {
    partes.push(`< ${condicao.valor_maximo}`);
  }
  return partes.length ? partes.join(' e ') : 'Qualquer valor';
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Duas regras por contexto, em ordem de avaliação, cobrindo os quatro
// contextos que a tela agrupa e uma regra inativa para mostrar a coluna
// "Ativo" com os dois valores.
const REGRAS_DE_ALCADA_MOCK: RegraDeAlcada[] = [
  {
    id: 'alcada-exemplo-01',
    codigo: 'DEF-INT-01',
    nome: 'Defesa intempestiva até R$ 5.000',
    contexto: 'DEFESA_INTEMPESTIVA',
    condicao: { valor_maximo: 5000 },
    papel_aprovador: 'GESTOR',
    exige_justificativa: true,
    prazo_aprovacao_dias_uteis: 5,
    ordem: 1,
    ativo: true,
  },
  {
    id: 'alcada-exemplo-02',
    codigo: 'DEF-INT-02',
    nome: 'Defesa intempestiva acima de R$ 5.000',
    contexto: 'DEFESA_INTEMPESTIVA',
    condicao: { valor_minimo: 5000 },
    papel_aprovador: 'GESTOR_ADMIN',
    exige_justificativa: true,
    prazo_aprovacao_dias_uteis: 10,
    ordem: 2,
    ativo: true,
  },
  {
    id: 'alcada-exemplo-03',
    codigo: 'GLOSA-RETRO-01',
    nome: 'Glosa retroativa em competência fechada',
    contexto: 'GLOSA_RETROATIVA',
    condicao: null,
    papel_aprovador: 'GESTOR_ADMIN',
    exige_justificativa: true,
    prazo_aprovacao_dias_uteis: 15,
    ordem: 1,
    ativo: true,
  },
  {
    id: 'alcada-exemplo-04',
    codigo: 'SUBST-LOTE-01',
    nome: 'Substituição em lote até 10 veículos',
    contexto: 'SUBSTITUICAO_LOTE',
    condicao: { valor_maximo: 10 },
    papel_aprovador: 'GESTOR',
    exige_justificativa: false,
    prazo_aprovacao_dias_uteis: 3,
    ordem: 1,
    ativo: true,
  },
  {
    id: 'alcada-exemplo-05',
    codigo: 'SUBST-LOTE-02',
    nome: 'Substituição em lote acima de 10 veículos',
    contexto: 'SUBSTITUICAO_LOTE',
    condicao: { valor_minimo: 10 },
    papel_aprovador: 'GESTOR_ADMIN',
    exige_justificativa: true,
    prazo_aprovacao_dias_uteis: 7,
    ordem: 2,
    ativo: false,
  },
  {
    id: 'alcada-exemplo-06',
    codigo: 'DEMO-POS-PAGO-01',
    nome: 'Alteração de demonstrativo já pago',
    contexto: 'ALTERACAO_DEMONSTRATIVO_POS_PAGO',
    condicao: null,
    papel_aprovador: 'GESTOR_ADMIN',
    exige_justificativa: true,
    prazo_aprovacao_dias_uteis: 20,
    ordem: 1,
    ativo: true,
  },
];

/** As regras do motor de alçadas, na ordem em que a API as avalia. */
export function useRegrasDeAlcada(): UseQueryResult<RegraDeAlcada[], ErroApi> {
  return useQuery<RegraDeAlcada[], ErroApi>({
    queryKey: CHAVES_DE_ALCADAS_GENERICAS.regras,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => REGRAS_DE_ALCADA_MOCK,
    retry: naoInsistirEmRecusa,
  });
}
